import { useState } from 'react';
import { useApi } from '@/api/hooks';
import styles from './Contracts.module.css'; // 既存CSSを流用

interface BillingRow {
  id: number;
  invoice_no: string;
  subtotal: number;
  tax: number;
  amount: number;
  allocated_amount: number;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  period_start: string;
  period_end: string;
  invoice_sent_at: string | null;
  plan_name: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  unpaid: '未払い',
  partial: '一部入金',
  paid: '支払済',
};

const STATUS_COLOR: Record<string, string> = {
  unpaid: '#f59e0b',
  partial: '#3b82f6',
  paid: '#22c55e',
};

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toISOString().slice(0, 10).replace(/-/g, '/');
}

export function TenantBilling() {
  const now = new Date();
  const [month, setMonth] = useState('');

  const url = month ? `/tenant/billing?month=${month}` : '/tenant/billing';
  const { data: bills, loading } = useApi<BillingRow[]>(url);

  const monthOptions: { value: string; label: string }[] = [{ value: '', label: '全期間' }];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthOptions.push({ value: val, label: `${d.getFullYear()}年${d.getMonth() + 1}月` });
  }

  const total = (bills ?? []).reduce((s, b) => s + b.amount, 0);
  const paid = (bills ?? []).filter(b => b.status === 'paid').reduce((s, b) => s + b.amount, 0);
  const unpaid = (bills ?? []).filter(b => b.status !== 'paid').reduce((s, b) => s + b.amount - (b.allocated_amount ?? 0), 0);

  return (
    <div className={styles.page}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>💳 請求・入金状況</h2>
        <select
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)' }}
          value={month} onChange={e => setMonth(e.target.value)}>
          {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* サマリーカード */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: '請求総額', value: `¥${total.toLocaleString()}`, color: '#1a1916' },
          { label: '入金済', value: `¥${paid.toLocaleString()}`, color: '#22c55e' },
          { label: '未入金残高', value: `¥${unpaid.toLocaleString()}`, color: unpaid > 0 ? '#f59e0b' : '#22c55e' },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* 請求一覧 */}
      {loading && <div style={{ color: 'var(--ink3)', fontSize: 13 }}>読み込み中...</div>}
      {!loading && (bills ?? []).length === 0 && (
        <div style={{ color: 'var(--ink3)', fontSize: 13, textAlign: 'center', padding: 32 }}>請求データがありません</div>
      )}
      {(bills ?? []).map(b => (
        <div key={b.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 18px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{b.invoice_no}</span>
              {b.plan_name && <span style={{ fontSize: 12, color: 'var(--ink3)', marginLeft: 8 }}>{b.plan_name}</span>}
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              background: `${STATUS_COLOR[b.status]}22`, color: STATUS_COLOR[b.status] }}>
              {STATUS_LABEL[b.status] ?? b.status}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 13, color: 'var(--ink2)' }}>
            <span>対象期間: {fmt(b.period_start)} ～ {fmt(b.period_end)}</span>
            <span>お支払期限: <span style={{ color: b.status === 'unpaid' && b.due_date && new Date(b.due_date) < new Date() ? '#ef4444' : undefined }}>{fmt(b.due_date)}</span></span>
            <span>請求金額（税込）: <strong>¥{b.amount.toLocaleString()}</strong></span>
            {b.status === 'partial' && (
              <span>入金済: ¥{(b.allocated_amount ?? 0).toLocaleString()} ／ 残: ¥{(b.amount - (b.allocated_amount ?? 0)).toLocaleString()}</span>
            )}
            {b.status === 'paid' && b.paid_at && (
              <span>入金日: {fmt(b.paid_at)}</span>
            )}
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
              小計 ¥{b.subtotal.toLocaleString()} ＋ 消費税 ¥{b.tax.toLocaleString()}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
